import type React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface DropdownProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  usePortal?: boolean;
  buttonRef?: React.RefObject<HTMLElement>;
}

export const Dropdown: React.FC<DropdownProps> = ({
  isOpen,
  onClose,
  children,
  className = "",
  usePortal = false,
  buttonRef,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, openUpward: false });

  useEffect(() => {
    if (isOpen && usePortal && buttonRef?.current) {
      const updatePosition = () => {
        if (!buttonRef.current) return;
        
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        
        // Estimate dropdown height (will be recalculated after render)
        const estimatedHeight = 200;
        
        // Open upward if there's not enough space below but enough space above
        const openUpward = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
        
        // Initial positioning
        setPosition({
          top: openUpward ? buttonRect.top - estimatedHeight - 8 : buttonRect.bottom + 8,
          left: buttonRect.left,
          openUpward,
        });
      };
      
      // Initial position calculation
      updatePosition();
      
      // Recalculate after dropdown is rendered to get actual height
      const recalculateAfterRender = () => {
        if (buttonRef.current && dropdownRef.current) {
          const buttonRect = buttonRef.current.getBoundingClientRect();
          const dropdownHeight = dropdownRef.current.offsetHeight;
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - buttonRect.bottom;
          const spaceAbove = buttonRect.top;
          
          const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
          
          setPosition({
            top: openUpward ? buttonRect.top - dropdownHeight - 8 : buttonRect.bottom + 8,
            left: buttonRect.left,
            openUpward,
          });
        }
      };
      
      // Use requestAnimationFrame and setTimeout to ensure dropdown is rendered
      const timeoutId = setTimeout(() => {
        requestAnimationFrame(recalculateAfterRender);
      }, 0);
      
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen, usePortal, buttonRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest(".dropdown-toggle")
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  if (!isOpen) return null;

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={`${usePortal ? 'fixed' : 'absolute'} z-40 ${usePortal ? '' : 'left-0 mt-2'} rounded-xl border border-gray-200 bg-white shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark ${className}`}
      style={usePortal ? { top: position.top, left: position.left } : undefined}
    >
      {children}
    </div>
  );

  if (usePortal) {
    return createPortal(dropdownContent, document.body);
  }

  return dropdownContent;
};
